import { useMutation, useQuery } from '@apollo/client/react';
import {
  Maximize2Icon,
  PencilIcon,
  PlusIcon,
  XIcon
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import {
  CREATE_GMMR,
  type CreateGmmrResponse,
  GET_GMMR_RECORDS_BY_ACCOUNT,
  type GetGmmrRecordsByAccountResponse,
  type GmmrRecordNode,
  UPDATE_GMMR,
  type UpdateGmmrResponse
} from './graphql';
import {
  NewMmriScoreDialog,
  type NewMmriScoreDialogFormValues
} from './NewMmriScoreDialog.tsx';
import { SectionHeader } from './section-ui';

import { Button } from '@/components/ui/button';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import clsx from 'clsx';

interface CreateGmmrVariables {
  accountId: number;
  values: Record<string, unknown>;
}

type MmriOverviewFormValues = NewMmriScoreDialogFormValues;

interface UpdateGmmrVariables {
  id: string;
  values: Record<string, unknown>;
}

const SECTION_KEYS_BY_TAB: Record<string, string[]> = {
  'accounting-reporting': [
    'complianceAdherence',
    'implementationPerformancePlan',
    'noDaysAfterEomCloseBooks',
    'noDaysReceiveAudit'
  ],
  'board-governance': [
    'annualPerformanceReview',
    'boardBooksDist48HrsAdvance',
    'boardQuality',
    'fourToSixBoardMeetingsAnnually',
    'leadDirector',
    'numberBoardCommittees',
    'numberIndependentDirectors',
    'positioningForExit',
    'relationshipWithSmc'
  ],
  'data-kpis': ['accountabilityForKpis', 'dataAndDashboardForDecisions', 'kpiReporting'],
  'loyalty-team-culture-esg': [
    'branding',
    'diverseEmployeeBase',
    'smcOperatingNetwork',
    'talentDevelopment'
  ],
  'management-competencies': [
    'budgetAchievement',
    'ceoCompetencyScore',
    'ceoPresence',
    'cfoCompetencyScore',
    'cmoCooCompetencyScore',
    'communicationWithEmployees',
    'croCompetencyScore',
    'ctoCompetencyScore',
    'customerSatisfaction',
    'midLevelEmployeeRetention',
    'noEmployeesEquityOwnership',
    'strategicPlanAdherence'
  ],
  overview: ['periodEnd'],
  'strategic-roadmap': ['objectivesAligned'],
  'technology-cybersecurity': [
    'cloudBasedSaas',
    'disasterPlan',
    'modernInfra',
    'socCompliantProtocol',
    'usingLedgerAndErpProvider'
  ]
};

const SUMMARY_SECTION_METRIC_BY_KEY: Partial<
  Record<
  string,
  { metric: string | string[]; section: string | string[] }
  >
> = {
  accountabilityForKpis: {
    metric: 'Clear Management Accountability for KPI Achievement',
    section: 'Data & KPIs'
  },
  annualPerformanceReview: {
    metric: 'Annual Performance Review',
    section: 'Board & Governance'
  },
  boardBooksDist48HrsAdvance: {
    metric: 'Board Books Distributed 48 Hours in Advance',
    section: 'Board & Governance'
  },
  boardQuality: {
    metric: 'Board Director Quality',
    section: 'Board & Governance'
  },
  branding: {
    metric: 'Branded Goods and Connection to Social Media Outlets to Support Business',
    section: 'Loyalty, Team Culture & ESG'
  },
  budgetAchievement: {
    metric: 'Budget Achievement',
    section: 'Management Competencies'
  },
  ceoCompetencyScore: {
    metric: ['CEO', 'CEO Competency Score'],
    section: 'Management Competencies'
  },
  ceoPresence: {
    metric: 'CEO Presence',
    section: 'Management Competencies'
  },
  cfoCompetencyScore: {
    metric: ['CFO', 'CFO Competency Score'],
    section: 'Management Competencies'
  },
  cloudBasedSaas: {
    metric: [
      'Utilization of Cloud-Based Software Applications (SaaS)',
      'Utilization of Cloud-Based Software Spplications (SaaS)'
    ],
    section: 'Technology & Cybersecurity'
  },
  cmoCooCompetencyScore: {
    metric: ['CMO/COO', 'CMO/COO Competency Score'],
    section: 'Management Competencies'
  },
  communicationWithEmployees: {
    metric: 'Communication with Employees',
    section: 'Management Competencies'
  },
  complianceAdherence: {
    metric: 'Compliance and Regulatory Standards Adherence',
    section: 'Accounting & Reporting'
  },
  croCompetencyScore: {
    metric: ['CRO (Head of Sales)', 'CRO Competency Score'],
    section: 'Management Competencies'
  },
  ctoCompetencyScore: {
    metric: ['CTO', 'CTO Competency Score'],
    section: 'Management Competencies'
  },
  customerSatisfaction: {
    metric: 'Customer Satisfaction',
    section: 'Management Competencies'
  },
  dataAndDashboardForDecisions: {
    metric: 'Utilizes Data and Dashboard to Make Real-Time Business Decisions',
    section: 'Data & KPIs'
  },
  disasterPlan: {
    metric: 'Fulsome Disaster Recovery Plan in Place',
    section: 'Technology & Cybersecurity'
  },
  diverseEmployeeBase: {
    metric: 'Diverse Employee Base',
    section: 'Loyalty, Team Culture & ESG'
  },
  fourToSixBoardMeetingsAnnually: {
    metric: '4-6 Board Meetings Annually',
    section: 'Board & Governance'
  },
  implementationPerformancePlan: {
    metric: 'Implementation of Performance-Driven Bonus Plan',
    section: 'Accounting & Reporting'
  },
  kpiReporting: {
    metric: 'KPI Reporting to Support Financial Performance',
    section: 'Data & KPIs'
  },
  leadDirector: {
    metric: 'Lead Director',
    section: 'Board & Governance'
  },
  midLevelEmployeeRetention: {
    metric: 'Mid-Level Employee Retention',
    section: 'Management Competencies'
  },
  modernInfra: {
    metric: 'Modern Technological Infrastructure',
    section: 'Technology & Cybersecurity'
  },
  noDaysAfterEomCloseBooks: {
    metric: 'Number of Days after End of Month to Close the Books',
    section: 'Accounting & Reporting'
  },
  noDaysReceiveAudit: {
    metric: 'Number of Days to Receive Audit',
    section: 'Accounting & Reporting'
  },
  noEmployeesEquityOwnership: {
    metric: 'Number of Employees Incentivized by Equity Ownership',
    section: 'Management Competencies'
  },
  numberBoardCommittees: {
    metric: 'Number of Board Committees implemented (Risk, Audit and Compensation)',
    section: 'Board & Governance'
  },
  numberIndependentDirectors: {
    metric: 'Number Independent Directors',
    section: 'Board & Governance'
  },
  objectivesAligned: {
    metric: 'Objectives Aligned with Market Dynamics & Priorities of Strategic Buyers',
    section: 'Strategic Roadmap'
  },
  positioningForExit: {
    metric: 'Positioning for Exit',
    section: 'Board & Governance'
  },
  relationshipWithSmc: {
    metric: 'Relationship with SMC',
    section: 'Board & Governance'
  },
  smcOperatingNetwork: {
    metric: 'Utilization of Star Mountain Operating Network',
    section: 'Loyalty, Team Culture & ESG'
  },
  socCompliantProtocol: {
    metric: ['Upgraded Security Protocols', 'SOC Compliant and Upgraded Security Protocols'],
    section: 'Technology & Cybersecurity'
  },
  strategicPlanAdherence: {
    metric: 'Strategic Plan Adherence',
    section: 'Management Competencies'
  },
  talentDevelopment: {
    metric: 'Utilization of Talent Development Services',
    section: 'Loyalty, Team Culture & ESG'
  },
  usingLedgerAndErpProvider: {
    metric: 'Using Proven Middle Market General Ledger and ERP Provider',
    section: 'Technology & Cybersecurity'
  }
};

export function MmriTab({
  targetEntityId
}: {
  targetEntityId:
    | {
        id: string;
        name: string;
      }
    | null
    | string
    | undefined;
}) {
  const targetEntityRawId =
    typeof targetEntityId === 'string' ? targetEntityId : targetEntityId?.id;

  const hasAssignedCompany = !!targetEntityRawId;
  const accountId = getAccountId(targetEntityRawId);

  const { data, loading, refetch } = useQuery<GetGmmrRecordsByAccountResponse>(
    GET_GMMR_RECORDS_BY_ACCOUNT,
    {
      skip: !targetEntityRawId || !accountId,
      variables: { id: accountId ?? 0 }
    }
  );
  const [createGmmr, { loading: isCreatingGmmr }] =
    useMutation<CreateGmmrResponse, CreateGmmrVariables>(CREATE_GMMR);
  const [updateGmmr, { loading: isUpdatingGmmr }] =
    useMutation<UpdateGmmrResponse, UpdateGmmrVariables>(UPDATE_GMMR);

  const rows = (data?.gmmrRecordsByAccount?.edges ?? []).map(edge => edge.node);
  const showCreateButton = hasAssignedCompany && rows.length !== 0;
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editDialogTab, setEditDialogTab] = useState('overview');
  const [editingRecord, setEditingRecord] = useState<GmmrRecordNode | null>(null);
  const [expandedRecordId, setExpandedRecordId] = useState<null | string>(null);
  const expandedRecord = rows.find(record => record.id === expandedRecordId);
  const editInitialValues = editingRecord
    ? mapRecordToFormValues(editingRecord, editDialogTab)
    : undefined;

  const openEditDialog = (record: GmmrRecordNode, tab: string) => {
    setEditingRecord(record);
    setEditDialogTab(tab);
    setIsEditDialogOpen(true);
  };

  const handleCreateGmmr = async (formValues: MmriOverviewFormValues) => {
    if (!accountId) return;
    if (!formValues.month || !formValues.year) {
      toast.error('Please select a month and year');
      return;
    }
    const sanitizedForm = buildGmmrValuesPayload(formValues);
    const nextIndex = rows.length + 1;
    sanitizedForm.name = `MMRI ${String(nextIndex).padStart(2, '0')}`;

    try {
      const { data: createData } = await createGmmr({
        variables: {
          accountId,
          values: sanitizedForm
        }
      });

      if (createData?.createGmmr.ok) {
        toast.success('MMRI report created successfully');
        setIsCreateDialogOpen(false);
        void refetch();
      } else {
        toast.error('Failed to create MMRI report');
      }
    } catch {
      toast.error('Failed to create MMRI report');
    }
  };

  const handleUpdateGmmr = async (formValues: MmriOverviewFormValues) => {
    if (!editingRecord?.id) return;
    if (
      editDialogTab === 'overview' &&
      (!formValues.month || !formValues.year)
    ) {
      toast.error('Please select a month and year');
      return;
    }

    const allowedKeys = SECTION_KEYS_BY_TAB[editDialogTab] ?? [];
    const sanitizedForm = buildGmmrValuesPayload(formValues, allowedKeys);

    try {
      const { data: updateData } = await updateGmmr({
        variables: {
          id: editingRecord.id,
          values: sanitizedForm
        }
      });

      if (updateData?.updateGmmr.ok) {
        toast.success('MMRI report updated successfully');
        setIsEditDialogOpen(false);
        setEditingRecord(null);
        void refetch();
      } else {
        toast.error('Failed to update MMRI report');
      }
    } catch {
      toast.error('Failed to update MMRI report');
    }
  };

  if (loading && hasAssignedCompany && !data) {
    return (
      <div className="grid place-content-center py-8">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-2.5">
      {!expandedRecord && <SectionHeader
        actions={
          showCreateButton ? (
            <Button
              onClick={() => {
                setIsCreateDialogOpen(true);
              }}
            >
              <PlusIcon />
              Score New MMRI
            </Button>
          ) : undefined
        }
        title="All MMRIs"
      />}
      <div>
        {!hasAssignedCompany ? (
          <Empty className="p-0!">
            <EmptyContent>
              <p className="text-muted-foreground text-center text-sm">
                A company should be assigned to this deal in order to create an
                MMRi
              </p>
            </EmptyContent>
          </Empty>
        ) : rows.length === 0 ? (
          <Empty className="p-0!">
            <EmptyContent className='py-[120px] border-0'>
              <p className="text-muted-foreground text-center text-sm">
                You haven't added any MMRI report yet. Get started by
                adding your report.
              </p>
              <Button
                onClick={() => {
                  setIsCreateDialogOpen(true);
                }}
              >
                <PlusIcon />
                Score New MMRI
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          <div className="space-y-2.5">
            <div className={clsx('p-4', !expandedRecord && 'border-border rounded-md border')}>
              {expandedRecord ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">
                      {expandedRecord.name ?? `MMRI ${String(rows.indexOf(expandedRecord) + 1).padStart(2, '0')}`}
                    </h3>
                    <Button
                      className="text-muted-foreground"
                      onClick={() => {
                        setExpandedRecordId(null);
                      }}
                      variant="outline"
                    >
                      <XIcon className="size-4" />
                    </Button>
                  </div>
                  <MmriExpandedView
                    onEdit={tab => {
                      openEditDialog(expandedRecord, tab);
                    }}
                    record={expandedRecord}
                  />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10" />
                      <TableHead>Name</TableHead>
                      <TableHead>Month</TableHead>
                      <TableHead>Scoring Date</TableHead>
                      <TableHead>Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((record, index) => {
                      const normalizedScore = normalizeScore(record.totalGmmrScore);
                      const overviewDate = getOverviewDate(record);

                      return (
                        <TableRow key={record.id}>
                          <TableCell>
                            <button
                              className="text-muted-foreground"
                              onClick={() => {
                                setExpandedRecordId(record.id);
                              }}
                              type="button"
                            >
                              <Maximize2Icon className="size-4" />
                            </button>
                          </TableCell>
                          <TableCell>{record.name ?? `MMRI ${String(index + 1).padStart(2, '0')}`}</TableCell>
                          <TableCell>{getMonthFromDate(overviewDate)}</TableCell>
                          <TableCell>{getYearFromDate(overviewDate)}</TableCell>
                          <TableCell
                            className="text-sm text-left font-bold"
                            style={{ color: getScoreColor(normalizedScore) }}
                          >
                            {formatScore(normalizedScore)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </div>
          </div>
        )}
      </div>
      <NewMmriScoreDialog
        isSaving={isCreatingGmmr}
        key="create-mmri-dialog"
        onOpenChange={setIsCreateDialogOpen}
        onSave={(values: MmriOverviewFormValues) => {
          void handleCreateGmmr(values);
        }}
        open={isCreateDialogOpen}
      />
      <NewMmriScoreDialog
        allowedTabs={[editDialogTab]}
        defaultTab={editDialogTab}
        initialValues={editInitialValues}
        isSaving={isUpdatingGmmr}
        key={`edit-mmri-dialog-${editingRecord?.id ?? 'none'}-${editDialogTab}`}
        onOpenChange={(open: boolean) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingRecord(null);
          }
        }}
        onSave={(values: MmriOverviewFormValues) => {
          void handleUpdateGmmr(values);
        }}
        open={isEditDialogOpen}
        title={`Edit ${toEditTitle(editDialogTab)}`}
      />
    </div>
  );
}

function booleanToYesNo(value: boolean | null | undefined) {
  if (value == null) return null;
  return value ? 'Yes' : 'No';
}

function buildGmmrValuesPayload(
  formValues: MmriOverviewFormValues,
  allowedKeys?: string[]
) {
  const form: Record<string, boolean | null | number | string | undefined> = {
    accountabilityForKpis: formValues.accountabilityForKpis,
    annualPerformanceReview: formValues.annualPerformanceReview,
    boardBooksDist48HrsAdvance: booleanToYesNo(
      formValues.boardBooksDist48HrsAdvance
    ),
    boardQuality: formValues.boardQuality,
    branding: booleanToYesNo(formValues.branding),
    budgetAchievement: formValues.budgetAchievement,
    ceoCompetencyScore: formValues.ceoCompetencyScore,
    ceoPresence: formValues.ceoPresence,
    cfoCompetencyScore: formValues.cfoCompetencyScore,
    cloudBasedSaas: formValues.cloudBasedSaas,
    cmoCooCompetencyScore: formValues.cmoCooCompetencyScore,
    communicationWithEmployees: formValues.communicationWithEmployees,
    complianceAdherence: formValues.complianceAdherence,
    croCompetencyScore: formValues.croCompetencyScore,
    ctoCompetencyScore: formValues.ctoCompetencyScore,
    customerSatisfaction: formValues.customerSatisfaction,
    dataAndDashboardForDecisions: formValues.dataAndDashboardForDecisions,
    disasterPlan: formValues.disasterPlan,
    diverseEmployeeBase: formValues.diverseEmployeeBase,
    fourToSixBoardMeetingsAnnually: booleanToYesNo(
      formValues.fourToSixBoardMeetingsAnnually
    ),
    implementationPerformancePlan: booleanToYesNo(
      formValues.implementationPerformancePlan
    ),
    kpiReporting: formValues.kpiReporting,
    leadDirector: formValues.leadDirector,
    midLevelEmployeeRetention: formValues.midLevelEmployeeRetention,
    modernInfra: formValues.modernInfra,
    noDaysAfterEomCloseBooks: formValues.noDaysAfterEomCloseBooks,
    noDaysReceiveAudit: formValues.noDaysReceiveAudit,
    noEmployeesEquityOwnership: formValues.noEmployeesEquityOwnership,
    numberBoardCommittees: formValues.numberBoardCommittees,
    numberIndependentDirectors: formValues.numberIndependentDirectors,
    objectivesAligned: booleanToYesNo(formValues.objectivesAligned),
    periodEnd: `${String(formValues.year)}-${String(formValues.month).padStart(2, '0')}-01`,
    positioningForExit: formValues.positioningForExit,
    relationshipWithSmc: formValues.relationshipWithSmc,
    smcOperatingNetwork: formValues.smcOperatingNetwork,
    socCompliantProtocol: formValues.socCompliantProtocol,
    strategicPlanAdherence: formValues.strategicPlanAdherence,
    talentDevelopment: formValues.talentDevelopment,
    usingLedgerAndErpProvider: formValues.usingLedgerAndErpProvider
  };

  const sanitized = sanitizeFormValues(form);
  if (!allowedKeys || allowedKeys.length === 0) return sanitized;

  return Object.fromEntries(
    Object.entries(sanitized).filter(([key]) => allowedKeys.includes(key))
  );
}

function FieldValue({
  itemScore,
  label,
  value
}: {
  itemScore?: null | number;
  label: string;
  value: string;
}) {
  const scoreOutOf5 =
    itemScore != null && !Number.isNaN(itemScore) ? itemScore : null;
  const normalizedForColor =
    scoreOutOf5 != null ? scoreOutOf5 / 5 : null;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold">{label}</span>
        {scoreOutOf5 != null && (
          <span
            className="text-sm font-bold"
            style={{
              color: getScoreColor(normalizedForColor)
            }}
          >
            {String(scoreOutOf5)}/5
          </span>
        )}
      </div>
      <div className="text-muted-foreground text-sm">{value}</div>
    </div>
  );
}

function formatScore(score: null | number) {
  if (score == null) return '-';
  return `${(score * 100).toFixed(1)}%`;
}

function formatSectionScore(score: null | number | undefined): string {
  if (score == null || Number.isNaN(score)) return '';
  const normalized = score > 1 ? score / 100 : score;
  const pct = Math.round(normalized * 100);
  return `${String(pct)}%`;
}

function getAccountId(rawId: null | string | undefined): null | number {
  if (!rawId) return null;

  if (/^\d+$/.test(rawId)) {
    return Number(rawId);
  }

  try {
    const decoded = atob(rawId);
    const numericPart = decoded.split(':').at(-1);
    if (numericPart && /^\d+$/.test(numericPart)) {
      return Number(numericPart);
    }
  } catch {
    return null;
  }

  return null;
}

function getMonthFromDate(value: null | string | undefined) {
  if (!value) return '-';

  const parts = value.split('-');
  if (parts.length < 2) return '-';
  const monthIndex = parseInt(parts[1], 10) - 1;
  if (Number.isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) return '-';

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  return monthNames[monthIndex];
}

function getMonthNumberFromDate(value: null | string | undefined) {
  if (!value) return null;

  const parts = value.split('-');
  if (parts.length < 2) return null;
  const month = parseInt(parts[1], 10);
  if (Number.isNaN(month) || month < 1 || month > 12) return null;
  return month;
}

function getOverviewDate(record: GmmrRecordNode) {
  const periodEnd = getSummaryRawValue(record.gmmrSummary, 'periodEnd');
  if (typeof periodEnd === 'string' && periodEnd.trim() !== '') {
    return periodEnd;
  }
  return record.date;
}

function getScoreColor(score: null | number) {
  if (score == null) return 'inherit';
  if (score >= 0.7) return '#059669';
  if (score >= 0.6) return '#E8BF1B';
  return '#DC2626';
}

function getSummaryItemScore(
  summary: null | Record<string, unknown> | string | undefined,
  key: string
): null | number {
  if (!summary) return null;

  const objectSummary = parseSummary(summary);
  if (!objectSummary) return null;

  const sectionMetric = SUMMARY_SECTION_METRIC_BY_KEY[key];
  if (!sectionMetric) return null;

  const sections = objectSummary.sections;
  if (!sections || typeof sections !== 'object' || Array.isArray(sections)) {
    return null;
  }

  const sectionsRecord = sections as Record<string, unknown>;
  const sectionNames = Array.isArray(sectionMetric.section)
    ? sectionMetric.section
    : [sectionMetric.section];
  const metrics = Array.isArray(sectionMetric.metric)
    ? sectionMetric.metric
    : [sectionMetric.metric];

  for (const sectionName of sectionNames) {
    const sectionRows = sectionsRecord[sectionName];
    if (!Array.isArray(sectionRows)) continue;

    const metricRow = sectionRows.find(
      (row): row is Record<string, unknown> => {
        if (!row || typeof row !== 'object') return false;
        const metric = (row as Record<string, unknown>).metric;
        if (typeof metric !== 'string') return false;
        return metrics.some(candidate => {
          return (
            normalizeMetricLabel(metric) === normalizeMetricLabel(candidate)
          );
        });
      }
    );

    if (metricRow) {
      const score = metricRow.score;
      if (typeof score === 'number' && !Number.isNaN(score)) return score;
      if (typeof score === 'string') {
        const n = Number(score);
        return Number.isNaN(n) ? null : n;
      }
      return null;
    }
  }

  for (const sectionRows of Object.values(sectionsRecord)) {
    if (!Array.isArray(sectionRows)) continue;
    const metricRow = sectionRows.find(
      (row): row is Record<string, unknown> => {
        if (!row || typeof row !== 'object') return false;
        const metric = (row as Record<string, unknown>).metric;
        if (typeof metric !== 'string') return false;
        return metrics.some(candidate => {
          return (
            normalizeMetricLabel(metric) === normalizeMetricLabel(candidate)
          );
        });
      }
    );
    if (metricRow) {
      const score = metricRow.score;
      if (typeof score === 'number' && !Number.isNaN(score)) return score;
      if (typeof score === 'string') {
        const n = Number(score);
        return Number.isNaN(n) ? null : n;
      }
      return null;
    }
  }

  return null;
}

function getSummaryRawValue(
  summary: null | Record<string, unknown> | string | undefined,
  key: string
) {
  if (!summary) return null;

  const objectSummary = parseSummary(summary);
  if (!objectSummary) return null;

  const sectionMetric = SUMMARY_SECTION_METRIC_BY_KEY[key];
  if (sectionMetric) {
    const sections = objectSummary.sections;

    if (sections && typeof sections === 'object' && !Array.isArray(sections)) {
      const sectionsRecord = sections as Record<string, unknown>;
      const sectionNames = Array.isArray(sectionMetric.section)
        ? sectionMetric.section
        : [sectionMetric.section];
      const metrics = Array.isArray(sectionMetric.metric)
        ? sectionMetric.metric
        : [sectionMetric.metric];

      for (const sectionName of sectionNames) {
        const sectionRows = sectionsRecord[sectionName];

        if (Array.isArray(sectionRows)) {
          const metricRow = sectionRows.find(
            (row): row is Record<string, unknown> => {
              if (!row || typeof row !== 'object') return false;
              const metric = (row as Record<string, unknown>).metric;
              if (typeof metric !== 'string') return false;

              return metrics.some(candidate => {
                return (
                  normalizeMetricLabel(metric) ===
                  normalizeMetricLabel(candidate)
                );
              });
            }
          );

          if (metricRow) {
            return metricRow.value;
          }
        }
      }

      // Fallback: search every section for matching metric aliases
      for (const sectionRows of Object.values(sectionsRecord)) {
        if (!Array.isArray(sectionRows)) continue;

        const metricRow = sectionRows.find((row): row is Record<string, unknown> => {
          if (!row || typeof row !== 'object') return false;
          const metric = (row as Record<string, unknown>).metric;
          if (typeof metric !== 'string') return false;
          return metrics.some(candidate => {
            return normalizeMetricLabel(metric) === normalizeMetricLabel(candidate);
          });
        });

        if (metricRow) {
          return metricRow.value;
        }
      }
    }
  }

  return (
    objectSummary[key] ??
    objectSummary[toSnakeCase(key)] ??
    objectSummary[toLegacyBoardMeetingsKey(key)] ??
    null
  );
}

function getSummaryValue(
  summary: null | Record<string, unknown> | string | undefined,
  key: string
) {
  const value = getSummaryRawValue(summary, key);

  if (value == null || value === '' || value === false) return '-';
  if (typeof value === 'boolean') return 'Yes';
  if (typeof value === 'number' || typeof value === 'string') {
    return String(value);
  }
  return '-';
}

function getYearFromDate(value: null | string | undefined) {
  if (!value) return '-';

  const parts = value.split('-');
  if (parts.length < 1) return '-';
  const year = parseInt(parts[0], 10);
  if (Number.isNaN(year)) return '-';

  return String(year);
}

function mapRecordToFormValues(
  record: GmmrRecordNode,
  tab: string
): Partial<MmriOverviewFormValues> {
  const overviewDate = getOverviewDate(record);
  const base: Partial<MmriOverviewFormValues> = {
    month: getMonthNumberFromDate(overviewDate),
    year: Number(getYearFromDate(overviewDate)) || null
  };
  if (tab === 'overview') {
    return base;
  }

  if (tab !== 'board-governance') {
    if (tab === 'accounting-reporting') {
      return {
        complianceAdherence: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'complianceAdherence')
        ),
        implementationPerformancePlan: parseBooleanishValue(
          getSummaryRawValue(record.gmmrSummary, 'implementationPerformancePlan')
        ),
        noDaysAfterEomCloseBooks: parseNumberishValue(
          getSummaryRawValue(record.gmmrSummary, 'noDaysAfterEomCloseBooks')
        ),
        noDaysReceiveAudit: parseNumberishValue(
          getSummaryRawValue(record.gmmrSummary, 'noDaysReceiveAudit')
        )
      };
    }

    if (tab === 'management-competencies') {
      return {
        budgetAchievement: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'budgetAchievement')
        ),
        ceoCompetencyScore: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'ceoCompetencyScore')
        ),
        ceoPresence: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'ceoPresence')
        ),
        cfoCompetencyScore: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'cfoCompetencyScore')
        ),
        cmoCooCompetencyScore: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'cmoCooCompetencyScore')
        ),
        communicationWithEmployees: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'communicationWithEmployees')
        ),
        croCompetencyScore: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'croCompetencyScore')
        ),
        ctoCompetencyScore: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'ctoCompetencyScore')
        ),
        customerSatisfaction: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'customerSatisfaction')
        ),
        midLevelEmployeeRetention: parseNumberishValue(
          getSummaryRawValue(record.gmmrSummary, 'midLevelEmployeeRetention')
        ),
        noEmployeesEquityOwnership: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'noEmployeesEquityOwnership')
        ),
        strategicPlanAdherence: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'strategicPlanAdherence')
        )
      };
    }

    if (tab === 'data-kpis') {
      return {
        accountabilityForKpis: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'accountabilityForKpis')
        ),
        dataAndDashboardForDecisions: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'dataAndDashboardForDecisions')
        ),
        kpiReporting: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'kpiReporting')
        )
      };
    }

    if (tab === 'strategic-roadmap') {
      return {
        objectivesAligned: parseBooleanishValue(
          getSummaryRawValue(record.gmmrSummary, 'objectivesAligned')
        )
      };
    }

    if (tab === 'technology-cybersecurity') {
      return {
        cloudBasedSaas: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'cloudBasedSaas')
        ),
        disasterPlan: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'disasterPlan')
        ),
        modernInfra: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'modernInfra')
        ),
        socCompliantProtocol: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'socCompliantProtocol')
        ),
        usingLedgerAndErpProvider: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'usingLedgerAndErpProvider')
        )
      };
    }

    if (tab === 'loyalty-team-culture-esg') {
      return {
        branding: parseBooleanishValue(
          getSummaryRawValue(record.gmmrSummary, 'branding')
        ),
        diverseEmployeeBase: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'diverseEmployeeBase')
        ),
        smcOperatingNetwork: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'smcOperatingNetwork')
        ),
        talentDevelopment: toNullableString(
          getSummaryRawValue(record.gmmrSummary, 'talentDevelopment')
        )
      };
    }

    return {};
  }

  const numberIndependentDirectors = getSummaryRawValue(
    record.gmmrSummary,
    'numberIndependentDirectors'
  );
  const numberBoardCommittees = getSummaryRawValue(
    record.gmmrSummary,
    'numberBoardCommittees'
  );
  const boardQuality = getSummaryRawValue(record.gmmrSummary, 'boardQuality');
  const positioningForExit = getSummaryRawValue(
    record.gmmrSummary,
    'positioningForExit'
  );
  const leadDirector = getSummaryRawValue(record.gmmrSummary, 'leadDirector');
  const annualPerformanceReview = getSummaryRawValue(
    record.gmmrSummary,
    'annualPerformanceReview'
  );
  const relationshipWithSmc = getSummaryRawValue(
    record.gmmrSummary,
    'relationshipWithSmc'
  );
  const boardBooksDist48HrsAdvance = getSummaryRawValue(
    record.gmmrSummary,
    'boardBooksDist48HrsAdvance'
  );
  const fourToSixBoardMeetingsAnnually = getSummaryRawValue(
    record.gmmrSummary,
    'fourToSixBoardMeetingsAnnually'
  );

  return {
    annualPerformanceReview:
      typeof annualPerformanceReview === 'string' ? annualPerformanceReview : null,
    boardBooksDist48HrsAdvance: parseBooleanishValue(boardBooksDist48HrsAdvance),
    boardQuality: typeof boardQuality === 'string' ? boardQuality : null,
    fourToSixBoardMeetingsAnnually: parseBooleanishValue(
      fourToSixBoardMeetingsAnnually
    ),
    leadDirector: typeof leadDirector === 'string' ? leadDirector : null,
    numberBoardCommittees:
      typeof numberBoardCommittees === 'string' ? numberBoardCommittees : null,
    numberIndependentDirectors:
      typeof numberIndependentDirectors === 'string'
        ? numberIndependentDirectors
        : null,
    positioningForExit:
      typeof positioningForExit === 'string' ? positioningForExit : null,
    relationshipWithSmc:
      typeof relationshipWithSmc === 'string' ? relationshipWithSmc : null
  };
}

function MmriExpandedView({
  onEdit,
  record
}: {
  onEdit: (tab: string) => void;
  record: GmmrRecordNode;
}) {
  const overviewDate = getOverviewDate(record);

  return (
    <div className="space-y-6 p-2">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Overview</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.totalGmmrScore) && (
              <span
                className="text-lg font-bold"
                style={{ color: getScoreColor(record.totalGmmrScore) }}
              >
                {formatSectionScore(record.totalGmmrScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('overview');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border grid grid-cols-2 gap-4 rounded-md border p-4 md:grid-cols-4">
          <div className="text-sm font-semibold">Month</div>
          <div className="text-muted-foreground text-sm">
            {getMonthFromDate(overviewDate)}
          </div>
          <div className="text-sm font-semibold">Year</div>
          <div className="text-muted-foreground text-sm">
            {getYearFromDate(overviewDate)}
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Board & Governance</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.boardGovernanceSectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(record.boardGovernanceSectionScore)
                }}
              >
                {formatSectionScore(record.boardGovernanceSectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('board-governance');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border space-y-3 rounded-md border p-4">
          <div className="text-sm font-semibold">
            Board consists of value-added, independent directors capable of
            helping the CEO and management team
          </div>
          <div className="text-muted-foreground text-sm">
            {getSummaryValue(record.gmmrSummary, 'boardQuality')}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'numberIndependentDirectors'
              )}
              label="Number of Independent Directors"
              value={getSummaryValue(record.gmmrSummary, 'numberIndependentDirectors')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'positioningForExit'
              )}
              label="Active and early in positioning the company for a successful exit"
              value={getSummaryValue(record.gmmrSummary, 'positioningForExit')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'fourToSixBoardMeetingsAnnually'
              )}
              label="4-6 Board Meetings Per Year"
              value={getSummaryValue(
                record.gmmrSummary,
                'fourToSixBoardMeetingsAnnually'
              )}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'numberBoardCommittees'
              )}
              label="Number of Board Committees implemented (Risk, Audit and Compensation)"
              value={getSummaryValue(record.gmmrSummary, 'numberBoardCommittees')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(record.gmmrSummary, 'leadDirector')}
              label="Presence of a lead director or Board Chair capable of managing the company on an interim basis in the event of an emergency or CEO termination/resignation"
              value={getSummaryValue(record.gmmrSummary, 'leadDirector')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'annualPerformanceReview'
              )}
              label="Annual performance and compensation reviews of the CEO, and other C Suite Members"
              value={getSummaryValue(record.gmmrSummary, 'annualPerformanceReview')}
            />
          </div>
          <FieldValue
            itemScore={getSummaryItemScore(
              record.gmmrSummary,
              'relationshipWithSmc'
            )}
            label="Values relationship with Star Mountain in situation where Star Mountain does not control the board"
            value={getSummaryValue(record.gmmrSummary, 'relationshipWithSmc')}
          />
          <FieldValue
            itemScore={getSummaryItemScore(
              record.gmmrSummary,
              'boardBooksDist48HrsAdvance'
            )}
            label="Board Books Distributed 48 Hours in Advance"
            value={getSummaryValue(record.gmmrSummary, 'boardBooksDist48HrsAdvance')}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Account & Reporting</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.accountingReportingSectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(record.accountingReportingSectionScore)
                }}
              >
                {formatSectionScore(record.accountingReportingSectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('accounting-reporting');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border rounded-md border p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'noDaysReceiveAudit'
              )}
              label="Number of Days to Receive Audit"
              value={getSummaryValue(record.gmmrSummary, 'noDaysReceiveAudit')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'noDaysAfterEomCloseBooks'
              )}
              label="Number of Days After End of Month to Close the Books"
              value={getSummaryValue(record.gmmrSummary, 'noDaysAfterEomCloseBooks')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'implementationPerformancePlan'
              )}
              label="TopImplementation of Performance-Driven Bonus Plan 5"
              value={getSummaryValue(record.gmmrSummary, 'implementationPerformancePlan')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'complianceAdherence'
              )}
              label="Compliance and Regulatory Standards Adherence"
              value={getSummaryValue(record.gmmrSummary, 'complianceAdherence')}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Management Competencies</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.managementCompetenciesSectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(
                    record.managementCompetenciesSectionScore
                  )
                }}
              >
                {formatSectionScore(record.managementCompetenciesSectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('management-competencies');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border space-y-3 rounded-md border p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'budgetAchievement'
              )}
              label="Achievement of budgeted annual / quarterly financial results"
              value={getSummaryValue(record.gmmrSummary, 'budgetAchievement')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'strategicPlanAdherence'
              )}
              label="Adoption of and adherence to a longer-term (3-5 year), Board-approved strategic plan"
              value={getSummaryValue(record.gmmrSummary, 'strategicPlanAdherence')}
            />
          </div>
          <FieldValue
            itemScore={getSummaryItemScore(record.gmmrSummary, 'ceoPresence')}
            label='External, market "presence" of the CEO to develop relationships with customers, prospects and potential acquirers'
            value={getSummaryValue(record.gmmrSummary, 'ceoPresence')}
          />
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'ceoCompetencyScore'
              )}
              label="CEO"
              value={getSummaryValue(record.gmmrSummary, 'ceoCompetencyScore')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'cfoCompetencyScore'
              )}
              label="CFO"
              value={getSummaryValue(record.gmmrSummary, 'cfoCompetencyScore')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'cmoCooCompetencyScore'
              )}
              label="CMO/COO"
              value={getSummaryValue(record.gmmrSummary, 'cmoCooCompetencyScore')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'ctoCompetencyScore'
              )}
              label="CTO"
              value={getSummaryValue(record.gmmrSummary, 'ctoCompetencyScore')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'croCompetencyScore'
              )}
              label="CRO (Head of Sales)"
              value={getSummaryValue(record.gmmrSummary, 'croCompetencyScore')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'midLevelEmployeeRetention'
              )}
              label="Midlevel Employee Retention & Tracked"
              value={(() => {
                const rawValue = getSummaryRawValue(
                  record.gmmrSummary,
                  'midLevelEmployeeRetention'
                );
                if (rawValue == null || rawValue === '') return '-';
                if (
                  typeof rawValue !== 'number' &&
                  typeof rawValue !== 'string'
                ) {
                  return '-';
                }

                const numericValue = Number(rawValue);
                if (Number.isNaN(numericValue)) return String(rawValue);

                return String(numericValue * 100);
              })()}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'noEmployeesEquityOwnership'
              )}
              label="Number of Employees Incentivized by Equity Ownership"
              value={getSummaryValue(record.gmmrSummary, 'noEmployeesEquityOwnership')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'communicationWithEmployees'
              )}
              label="Frequent and effective communications with employees."
              value={getSummaryValue(record.gmmrSummary, 'communicationWithEmployees')}
            />
          </div>
          <FieldValue
            itemScore={getSummaryItemScore(
              record.gmmrSummary,
              'customerSatisfaction'
            )}
            label="Customer satisfaction / external feedback scores (e.g., NPS)"
            value={getSummaryValue(record.gmmrSummary, 'customerSatisfaction')}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Data & KPIs</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.dataKpisSectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(record.dataKpisSectionScore)
                }}
              >
                {formatSectionScore(record.dataKpisSectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('data-kpis');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border rounded-md border p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'dataAndDashboardForDecisions'
              )}
              label="Utilizes Data and Dashboard to Make Real-Time Business Decisions"
              value={getSummaryValue(record.gmmrSummary, 'dataAndDashboardForDecisions')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'kpiReporting'
              )}
              label="KPI Reporting to Support Financial Performance"
              value={getSummaryValue(record.gmmrSummary, 'kpiReporting')}
            />
          </div>
          <div className="mt-4">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'accountabilityForKpis'
              )}
              label="Clear Management Accountability for KPI Achievement"
              value={getSummaryValue(record.gmmrSummary, 'accountabilityForKpis')}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Strategic Roadmap</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.strategicRoadmapSectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(record.strategicRoadmapSectionScore)
                }}
              >
                {formatSectionScore(record.strategicRoadmapSectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('strategic-roadmap');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border rounded-md border p-4">
          <FieldValue
            itemScore={getSummaryItemScore(
              record.gmmrSummary,
              'objectivesAligned'
            )}
            label="Objectives Aligned with Market Dynamics & Priorities of Strategic Buyers"
            value={getSummaryValue(record.gmmrSummary, 'objectivesAligned')}
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Technology & Cybersecurity</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.technologyCybersecuritySectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(
                    record.technologyCybersecuritySectionScore
                  )
                }}
              >
                {formatSectionScore(record.technologyCybersecuritySectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('technology-cybersecurity');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border rounded-md border p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'usingLedgerAndErpProvider'
              )}
              label="Using Proven Middle Market General Ledger and ERP Provider"
              value={getSummaryValue(record.gmmrSummary, 'usingLedgerAndErpProvider')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'cloudBasedSaas'
              )}
              label="Utilization of Cloud-Based Software Applications (SaaS)"
              value={getSummaryValue(record.gmmrSummary, 'cloudBasedSaas')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'modernInfra'
              )}
              label="Modern Technological Infrastructure"
              value={getSummaryValue(record.gmmrSummary, 'modernInfra')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'socCompliantProtocol'
              )}
              label="Upgraded Security Protocols"
              value={getSummaryValue(record.gmmrSummary, 'socCompliantProtocol')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'disasterPlan'
              )}
              label="Fulsome Disaster Recovery Plan in Place"
              value={getSummaryValue(record.gmmrSummary, 'disasterPlan')}
            />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-xl font-semibold">Loyalty, Team Culture & ESG</h4>
          <div className="flex items-center gap-2">
            {formatSectionScore(record.loyaltyTeamCultureEsgSectionScore) && (
              <span
                className="text-lg font-bold"
                style={{
                  color: getScoreColor(
                    record.loyaltyTeamCultureEsgSectionScore
                  )
                }}
              >
                {formatSectionScore(record.loyaltyTeamCultureEsgSectionScore)}
              </span>
            )}
            <Button
              onClick={() => {
                onEdit('loyalty-team-culture-esg');
              }}
              size="sm"
              type="button"
              variant="ghost"
            >
              <PencilIcon className="size-4" />
              Edit
            </Button>
          </div>
        </div>
        <div className="border-border rounded-md border p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'talentDevelopment'
              )}
              label="Utilization of Talent Development Services"
              value={getSummaryValue(record.gmmrSummary, 'talentDevelopment')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'smcOperatingNetwork'
              )}
              label="Utilization of Star Mountain Operating Network"
              value={getSummaryValue(record.gmmrSummary, 'smcOperatingNetwork')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(record.gmmrSummary, 'branding')}
              label="Branded Goods and Connection to Social Media Outlets to Support Business"
              value={getSummaryValue(record.gmmrSummary, 'branding')}
            />
            <FieldValue
              itemScore={getSummaryItemScore(
                record.gmmrSummary,
                'diverseEmployeeBase'
              )}
              label="Diverse Employee Base"
              value={getSummaryValue(record.gmmrSummary, 'diverseEmployeeBase')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function normalizeMetricLabel(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function normalizeScore(value: null | number | undefined) {
  if (value == null || Number.isNaN(value)) return null;
  return value > 1 ? value / 100 : value;
}

function parseBooleanishValue(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value ? true : null;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'yes' || normalized === 'true') return true;
  if (normalized === 'no' || normalized === 'false') return false;
  return null;
}

function parseNumberishValue(value: unknown): null | number {
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value !== 'string') return null;
  const nextValue = Number(value);
  return Number.isNaN(nextValue) ? null : nextValue;
}

function parseSummary(summary: Record<string, unknown> | string) {
  if (typeof summary === 'string') {
    try {
      const parsed: unknown = JSON.parse(summary);
      if (parsed && typeof parsed === 'object') {
        return parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
    return null;
  }

  return summary;
}

function sanitizeFormValues(
  values: Record<string, boolean | null | number | string | undefined>
) {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => {
      return !(
        value === null ||
        value === undefined ||
        (typeof value === 'number' && Number.isNaN(value))
      );
    })
  );
}

function toEditTitle(tab: string) {
  const tabMeta = {
    'accounting-reporting': 'Accounting & Reporting',
    'board-governance': 'Board & Governance',
    'data-kpis': 'Data & KPIs',
    'loyalty-team-culture-esg': 'Loyalty, Team Culture & ESG',
    'management-competencies': 'Management Competencies',
    overview: 'Overview',
    'strategic-roadmap': 'Strategic Roadmap',
    'technology-cybersecurity': 'Technology & Cybersecurity'
  } as const;

  if (tab in tabMeta) {
    return tabMeta[tab as keyof typeof tabMeta];
  }
  return 'MMRI';
}

function toLegacyBoardMeetingsKey(key: string) {
  if (key === 'fourToSixBoardMeetingsAnnually') {
    return '_4To6BoardMeetingsAnnually';
  }
  return key;
}

function toNullableString(value: unknown): null | string {
  return typeof value === 'string' ? value : null;
}

function toSnakeCase(value: string) {
  return value.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
}
